import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Calendar, User, Clock } from "lucide-react";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { HreflangTags } from "@/components/HreflangTags";

const blogPosts = [
  {
    slug: "ai-symptom-checker-hospitals",
    title: "How AI Symptom Checkers Are Transforming Hospital Triage",
    excerpt: "Discover how artificial intelligence is revolutionizing the way hospitals assess and prioritize patients, reducing wait times and improving outcomes.",
    author: "Ranvir Gill",
    date: "December 15, 2024",
    readTime: "5 min read",
    category: "AI in Healthcare"
  },
  {
    slug: "reduce-er-wait-times",
    title: "5 Proven Strategies to Reduce Emergency Room Wait Times",
    excerpt: "Learn how healthcare facilities are cutting ER wait times by up to 50% using intelligent triage systems and patient flow optimization.",
    author: "Ranvir Gill",
    date: "December 10, 2024",
    readTime: "7 min read",
    category: "Hospital Operations"
  },
  {
    slug: "digital-front-door-healthcare",
    title: "The Digital Front Door: Reimagining Healthcare Access",
    excerpt: "How digital-first patient engagement is creating seamless healthcare experiences from the first symptom to treatment completion.",
    author: "Ranvir Gill",
    date: "December 5, 2024",
    readTime: "6 min read",
    category: "Digital Health"
  },
  {
    slug: "prevent-unnecessary-er-visits",
    title: "Preventing Unnecessary ER Visits: A Guide for Patients and Providers",
    excerpt: "Understanding when to go to the ER vs. urgent care can save lives, time, and billions in healthcare costs annually.",
    author: "Ranvir Gill",
    date: "November 28, 2024",
    readTime: "8 min read",
    category: "Patient Education"
  },
  {
    slug: "healthcare-triage-automation",
    title: "Healthcare Triage Automation: The Future of Patient Assessment",
    excerpt: "Explore how automated triage systems are helping healthcare providers make faster, more accurate decisions about patient care priorities.",
    author: "Ranvir Gill",
    date: "November 20, 2024",
    readTime: "6 min read",
    category: "Healthcare Technology"
  },
  {
    slug: "virtual-healthcare-triage-system",
    title: "Virtual Healthcare Triage Systems: Transforming Remote Patient Care",
    excerpt: "How virtual triage systems are expanding healthcare access and improving patient outcomes through remote patient assessment technology.",
    author: "Ranvir Gill",
    date: "November 15, 2024",
    readTime: "7 min read",
    category: "Telehealth"
  },
  {
    slug: "clinical-decision-support-tool",
    title: "Clinical Decision Support Tools: Enhancing Healthcare Quality",
    excerpt: "How clinical decision support tools improve patient outcomes, reduce medical errors, and help healthcare providers make evidence-based decisions.",
    author: "Ranvir Gill",
    date: "November 10, 2024",
    readTime: "8 min read",
    category: "Clinical Technology"
  },
  {
    slug: "patient-intake-software",
    title: "Patient Intake Software: Streamlining the Healthcare Front Door",
    excerpt: "How patient intake software modernizes healthcare registration, reduces wait times, and improves data accuracy for better patient experiences.",
    author: "Ranvir Gill",
    date: "November 5, 2024",
    readTime: "6 min read",
    category: "Healthcare Operations"
  },
  {
    slug: "hospital-triage-automation",
    title: "Hospital Triage Automation: Revolutionizing Emergency Department Efficiency",
    excerpt: "How hospital triage automation is transforming emergency departments, reducing wait times, and improving patient outcomes through AI technology.",
    author: "Ranvir Gill",
    date: "October 28, 2024",
    readTime: "7 min read",
    category: "Emergency Medicine"
  }
];

export default function BlogPage() {
  return (
    <>
      <Helmet>
        <title>Healthcare Insights Blog by Ranvir Gill | CheckFirst</title>
        <meta
          name="description"
          content="Expert insights on AI in healthcare, patient triage, reducing ER wait times, and digital health innovation from Ranvir Gill, founder of CheckFirst."
        />
        <meta
          name="keywords"
          content="Ranvir Gill blog, healthcare technology blog, AI symptom checker, reduce ER wait times, digital front door healthcare, healthcare triage automation, patient triage insights"
        />
        <meta name="author" content="Ranvir Gill" />
        
        <meta property="og:title" content="Healthcare Insights Blog by Ranvir Gill | CheckFirst" />
        <meta property="og:description" content="Expert insights on AI in healthcare, patient triage, and digital health innovation from Ranvir Gill." />
        <meta property="og:url" content="https://www.checkfirstcare.com/blog" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.checkfirstcare.com/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        
        <meta name="twitter:image" content="https://www.checkfirstcare.com/og-image.jpg" />
        
        <link rel="canonical" href="https://www.checkfirstcare.com/blog" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "CheckFirst Healthcare Insights",
            "description": "Expert insights on AI in healthcare and patient triage from Ranvir Gill",
            "url": "https://www.checkfirstcare.com/blog",
            "author": {
              "@type": "Person",
              "name": "Ranvir Gill",
              "jobTitle": "Founder & CEO",
              "image": "https://www.checkfirstcare.com/ranvir-gill.jpg",
              "url": "https://www.checkfirstcare.com/about",
              "worksFor": {
                "@type": "Organization",
                "name": "CheckFirst"
              }
            },
            "publisher": {
              "@type": "Organization",
              "name": "CheckFirst",
              "logo": "https://www.checkfirstcare.com/logo.png"
            }
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://www.checkfirstcare.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Blog",
                "item": "https://www.checkfirstcare.com/blog"
              }
            ]
          })}
        </script>
      </Helmet>
      <HreflangTags />

      <div className="min-h-screen">
        {/* Breadcrumbs */}
        <div className="container mx-auto px-4 lg:px-8 pt-6">
          <Breadcrumbs items={[{ label: 'Blog' }]} />
        </div>

        {/* Hero Section */}
        <section className="py-12 lg:py-20 bg-gradient-to-br from-primary/10 to-secondary/30">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto text-center space-y-6">
              <h1 className="text-4xl lg:text-5xl font-bold text-foreground">
                Healthcare <span className="text-primary">Insights</span>
              </h1>
              <p className="text-xl text-muted-foreground">
                Expert perspectives on AI in healthcare, patient triage, and digital health innovation from{" "}
                <Link to="/about" className="text-primary hover:underline font-medium">Ranvir Gill</Link>{" "}
                and the CheckFirst team.
              </p>
            </div>
          </div>
        </section>

        {/* Blog Posts Grid */}
        <section className="py-12 lg:py-16">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
              {blogPosts.map((post) => (
                <Card key={post.slug} className="shadow-card hover:shadow-card-hover transition-smooth overflow-hidden group">
                  <CardContent className="p-0">
                    <div 
                      className="h-48 bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center"
                      role="img"
                      aria-label={`${post.category} article thumbnail for: ${post.title}`}
                    >
                      <span className="text-sm font-medium text-primary bg-primary/10 px-3 py-1 rounded-full">
                        {post.category}
                      </span>
                    </div>
                    <div className="p-6 space-y-4">
                      <h2 className="text-xl font-bold text-foreground group-hover:text-primary transition-colors line-clamp-2">
                        <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                      </h2>
                      <p className="text-muted-foreground text-sm line-clamp-3">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <Link to="/about" className="flex items-center gap-1 hover:text-primary transition-colors">
                          <User className="h-3 w-3" />
                          {post.author}
                        </Link>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {post.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {post.readTime}
                        </span>
                      </div>
                      <Button variant="ghost" size="sm" asChild className="p-0 h-auto text-primary hover:text-primary/80">
                        <Link to={`/blog/${post.slug}`}>
                          Read More <ArrowRight className="ml-1 h-4 w-4" />
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 lg:py-16 bg-secondary/50">
          <div className="container mx-auto px-4 lg:px-8 text-center">
            <div className="max-w-2xl mx-auto space-y-6">
              <h2 className="text-2xl lg:text-3xl font-bold text-foreground">
                Ready to Transform Your Healthcare Triage?
              </h2>
              <p className="text-muted-foreground">
                See how CheckFirst can help reduce ER wait times and improve patient outcomes.
              </p>
              <Button asChild size="lg">
                <Link to="/contact">
                  Get Started <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
